// dependencies
import { WritableDraft } from "immer/dist/internal";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { GenerateProcess } from "redux-process";
import { useImmer } from "use-immer";

import { AdminContainer, AdminTitle } from "../../../components/Admin";
import BeforeUnload from "../../../components/BeforeUnload";
// components
import Breadcrumbs from "../../../components/Breadcrumbs";
import Button from "../../../components/Button";
import DropDownMenu from "../../../components/DropDownMenu";
import EasyAddFormCard from "../../../components/EasyAddFormCard";
import { Grid, GridCol } from "../../../components/Grid";
import TextInput from "../../../components/Inputs/TextInput";
import PageTitle from "../../../components/PageTitle";
import ReviewFormCard from "../../../components/ReviewFormCard";
import ShowError from "../../../components/ShowError";
import Spacer from "../../../components/Spacer";
import Tabs from "../../../components/Tabs";
// config
import Config from "./Config";

import { useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";

import { DisplayConfirmationModal } from "../../../actions/modals.actions";
// actions
import { ChangePage, HardPageLoad } from "../../../actions/navigation.actions";
import { DisplayError, DisplaySuccess } from "../../../actions/notifications.actions";

// selectors
import { getUserCompanyName } from "../../../selectors/app";
import { getURLParam } from "../../../selectors/navigation";
import { getCanUserEditRedactedness } from "../../../selectors/permissions";
import { getCompanySettingsContext } from "../../../selectors/settings";

// utils
import { checkDuplicateEasyAdd, getFormsExcludingIsEditing } from "./utils";

// styles
import css from "./EditReviewTemplate.module.scss";

interface Form {
	id: string;
	name: string;
	isActive?: boolean;
}

interface EasyAddForm {
	_id: string;
	name: string;
}

interface Person {
	id: string;
	name: string;
}

interface EasyAddForm {
	id: string;
	formID: string;
	formObject: EasyAddForm | null;
	who_can_add_form: {
		any_employee: boolean;
		subject: boolean;
		manager_or_chain_of_command: boolean;
		admin_or_group_admin: boolean;
	};
	subject_employee_choose_author: string;
	who_signs_off: Person[];
	author_due_date: number;
	signer_due_date: number;
	due_date_types: "relative" | "static";
	redact: boolean;
	visibility: boolean;
	allow_comments: boolean;
	is_active: boolean;
	requiresApproval: boolean;
	unsaved: boolean;
	editableForm: Form | null;
	isEditing: boolean;
	isNewForm: boolean;
	isFormDeleted: boolean;
}

interface BasicForm {
	id: string;
	type: "basic";
	form: Form | null;
	assignment: {
		authors: Person[];
		signers: Person[];
		redacted: boolean;
		daysToCompleteAuthors: number;
		daysToCompleteSigners: number;
		visibleToSubject: boolean;
	};
	isEditing: boolean;
	editableForm: Form | null;
	dateType: "relative" | "static";
	authorRequired: boolean;
	unsaved: boolean;
	isNewForm: boolean;
	isFormDeleted: boolean;
}

interface ExternalForm {
	id: string;
	type: "external";
	form: Form | null;
	assignment: {
		authorName: string;
		authorEmail: string;
		emailMessage: string;
		visibleToSubject: boolean;
		redacted: boolean;
	};
	isEditing: boolean;
	editableForm: Form | null;
	unsaved: boolean;
	isNewForm: boolean;
	isFormDeleted: boolean;
}

export interface TemplateForms {
	basicForms: BasicForm[];
	externalForms: ExternalForm[];
	easyAddForms: EasyAddForm[];
}
type TemplateFormKeys = keyof TemplateForms;
type LockedField = "redactedness" | "activeFormToggle" | "dateType";

const EditReviewTemplate = () => {
	// true template data that is saved in DB
	const [templateName, setTemplateName] = useState<string>("New Template");
	const [templateForms, setTemplateForms] = useState<TemplateForms>({
		basicForms: [],
		externalForms: [],
		easyAddForms: [],
	});

	// local template data
	const [localTemplateName, setLocalTemplateName] = useState<string>("New Template");
	const [localTemplateForms, setLocalTemplateForms] = useImmer<TemplateForms>({
		basicForms: [],
		externalForms: [],
		easyAddForms: [],
	});

	// form components
	const [formsComponents, setFormsComponents] = useState<JSX.Element[]>([]);

	const [duplicateEasyAdd, setDuplicateEasyAdd] = useState<boolean>(false);

	const [selectedFormType, setSelectedFormType] = useState<"basicForms" | "easyAddForms" | "externalForms">(
		"basicForms"
	);
	const [isSaving, setIsSaving] = useState<boolean>(false);

	const dispatch = useDispatch<any>();

	const companyName = useSelector(getUserCompanyName);
	const templateID = useSelector((state) => getURLParam(state, "templateID")) as string | undefined;
	const canUserChangeRedactedness = useSelector((state) => getCanUserEditRedactedness(state));
	const easyAddEnabled = useSelector(getCompanySettingsContext).getIn(["reviews", "easy_add"], false);
	const { value: ff360Reviews } = useFeatureFlagQuery("ff-is-three-sixty-reviews-enabled", false);
	// We never want to allow the user to change the active form status from the review template page.
	// They should only be able to change the active form status when creating a review cycle
	const alwaysLockedFields: LockedField[] = ["activeFormToggle"];

	const lockedFields: LockedField[] = canUserChangeRedactedness
		? alwaysLockedFields
		: [...alwaysLockedFields, "redactedness"];

	let menuOptions = Config.menuOptions;

	let tabTitles = [
		{
			id: "basicForms",
			label: "Forms",
			size: localTemplateForms.basicForms.filter((form) => !form.isFormDeleted).length,
		},
		{
			id: "easyAddForms",
			label: "360 Forms",
			size: localTemplateForms.easyAddForms.filter((form) => !form.isFormDeleted).length,
		},
		{
			id: "externalForms",
			label: "External Forms",
			size: localTemplateForms.externalForms.filter((form) => !form.isFormDeleted).length,
		},
	];

	// filter out menu options and tab titles
	if (!templateID) {
		menuOptions = menuOptions.filter((option) => option.name !== "delete");
	}
	if (!easyAddEnabled || !ff360Reviews) {
		menuOptions = menuOptions.filter((option) => option.name !== "addEasyAddForm");
		tabTitles = tabTitles.filter((title) => title.id !== "easyAddForms");
	}

	useEffect(() => {
		const newFormsComponents = getFormComponents(localTemplateForms[selectedFormType]);
		if (newFormsComponents) {
			setFormsComponents(newFormsComponents as JSX.Element[]);
		} else {
			setFormsComponents([]);
		}
	}, [JSON.stringify(localTemplateForms[selectedFormType])]);

	// if templateID exists, set up template data
	// also fetches permissions
	useEffect(() => {
		const getTemplateData = async () => {
			const response = await dispatch(GenerateProcess("FetchTemplate", { templateID }));
			if (response.response) {
				const template = response.response.toJS().reviewTemplate;
				const templateForms = template.assignments.map((form) => ({
					isNewForm: false,
					unsaved: false,
					isEditing: false,
					...form,
				}));

				const basic = templateForms.filter((form) => form.type === "basic");
				const external = templateForms.filter((form) => form.type === "external");
				const easyAdd = templateForms.filter((form) => form.type === "easyAdd");

				setLocalTemplateForms((draft) => {
					draft.basicForms = basic;
					draft.externalForms = external;
					draft.easyAddForms = easyAdd;
				});
				setTemplateForms({
					basicForms: basic,
					externalForms: external,
					easyAddForms: easyAdd,
				});

				setTemplateName(template.name);
				setLocalTemplateName(template.name);
			}
		};
		if (templateID) {
			getTemplateData();
		}
		// fetch permissions
		dispatch(GenerateProcess("GetDomainPermissions", { domain: "review-cycles" }));
	}, [templateID]);

	// check for template changes by seeing if the template name or forms have changed
	const templateHasChanges = () => {
		// compare local template name to actual saved template name
		if (templateName !== localTemplateName || isSaving) {
			return true;
		}

		// compared localTemplate forms with actual saved templateForms
		const filteredLocalTemplateForms = getFormsExcludingIsEditing(localTemplateForms);
		const filteredTemplateForms = getFormsExcludingIsEditing(templateForms);

		// loop through each form type to compare, until finding unsaved changes
		const types = Object.keys(filteredTemplateForms) as Array<keyof TemplateForms>;
		for (let i = 0; i < types.length; i++) {
			const formType = types[i];
			const localForms = filteredLocalTemplateForms[formType].filter((form) => !form.isFormDeleted);
			const savedForms = filteredTemplateForms[formType].filter((form) => !form.isFormDeleted);
			if (JSON.stringify(localForms) !== JSON.stringify(savedForms)) {
				return true;
			}
		}
		return false;
	};

	const handleSaveForm = (form, formIndex) => {
		let formToSave: BasicForm | ExternalForm | EasyAddForm | object = {};
		if (selectedFormType === "basicForms") {
			formToSave = {
				assignment: {
					authors: form.authors,
					signers: form.signers,
					daysToCompleteAuthors: form.authorsDueDate,
					daysToCompleteSigners: form.signersDueDate,
					redacted: form.redacted,
					visibleToSubject: form.visibleToSubject,
				},
				form: form.form,
				isEditing: false,
				unsaved: false,
				isNewForm: false,
				type: "basic",
			};
		}
		if (selectedFormType === "externalForms") {
			formToSave = {
				assignment: {
					authorEmail: form.authorEmail,
					authorName: form.authorName,
					emailMessage: form.emailMessage,
					redacted: form.redacted,
					visibleToSubject: form.visibleToSubject,
				},
				form: form.form,
				isEditing: false,
				unsaved: false,
				isNewForm: false,
				type: "external",
			};
		}
		if (selectedFormType === "easyAddForms") {
			const hasDuplicate = checkDuplicateEasyAdd(form, localTemplateForms.easyAddForms, formIndex);

			if (hasDuplicate) {
				setDuplicateEasyAdd(true);
				setTimeout(() => {
					setDuplicateEasyAdd(false);
				}, 5000);

				return;
			}
			formToSave = {
				formID: form.form.id,
				formObject: {
					_id: form.form.id || form.form._id,
					name: form.form.name,
				},
				who_can_add_form: form.who_can_add_form,
				subject_employee_choose_author: form.subject_employee_choose_author,
				who_signs_off: form.who_signs_off,
				author_due_date: form.author_due_date,
				signer_due_date: form.signer_due_date,
				due_date_types: form.due_date_types,
				redact: form.redact,
				visibility: form.visibility,
				allow_comments: form.allow_comments,
				is_active: form.is_active,
				requiresApproval: form.requiresApproval,
				unsaved: false,
				isEditing: false,
				isNewForm: false,
			};
		}
		if (formToSave !== null) {
			setLocalTemplateForms((draft) => {
				draft[selectedFormType][formIndex] = formToSave as WritableDraft<
					BasicForm | ExternalForm | EasyAddForm
				>;
			});
		}
	};

	const handleDeleteForm = (formIndex: number) => {
		setLocalTemplateForms((draft) => {
			draft[selectedFormType][formIndex].isFormDeleted = true;
		});
	};

	const handleAddForm = (formType: string) => {
		setLocalTemplateForms((draft) => {
			// add an empty form to appropriate form list
			// to create new form of that type
			draft[formType].push(Config.createFormValues[formType]);
		});
	};

	const toggleEditForm = (formIndex: number) => {
		setLocalTemplateForms((draft) => {
			const isEditing = draft[selectedFormType][formIndex].isEditing;
			draft[selectedFormType][formIndex].isEditing = !isEditing;
		});
	};

	const formatTemplateForms = (
		basicForms: BasicForm[],
		externalForms: ExternalForm[],
		easyAddForms: EasyAddForm[]
	) => {
		const formattedBasicForms = basicForms
			.filter((form) => form.unsaved !== true && form.isFormDeleted !== true)
			.map((basicForm) => {
				const { form, assignment } = basicForm;
				const id = basicForm.id || null;
				const { authors, signers, daysToCompleteAuthors, daysToCompleteSigners, visibleToSubject, redacted } =
					assignment;
				// QUESTION:
				// is it okay to allow form and name to optionally be undefined?
				// or should we add checks to only return if those values exist
				return {
					id,
					form: form?.id || null,
					name: form?.name,
					authors: authors.map((author) => author.id),
					signers: signers.map((signer) => signer.id),
					daysToCompleteAuthors,
					daysToCompleteSigners,
					visibleToSubject,
					redacted,
				};
			});
		const formattedExternalForms = externalForms
			.filter((form) => form.unsaved !== true && form.isFormDeleted !== true)
			.map((externalForm) => {
				const { form, assignment } = externalForm;
				const id = externalForm?.id || null;
				const { authorEmail, authorName, emailMessage, visibleToSubject, redacted } = assignment;
				return {
					id,
					form: form?.id || null,
					name: form?.name || null,
					"external-author-name": authorName,
					email: authorEmail,
					"email-body": emailMessage,
					visibleToSubject,
					redacted,
				};
			});
		const formattedEasyAddForms = easyAddForms
			.filter((form) => form.unsaved !== true && form.isFormDeleted !== true)
			.map((easyAddForm) => {
				return {
					id: easyAddForm.id || null,
					form: easyAddForm?.formObject?._id,
					name: easyAddForm?.formObject?.name,
					whoCanAddForm: easyAddForm.who_can_add_form,
					subjectEmployeeChooseAuthor: easyAddForm.subject_employee_choose_author,
					signers: easyAddForm.who_signs_off.map((signer) => signer.id),
					daysToCompleteAuthors: easyAddForm.author_due_date,
					daysToCompleteSigners: easyAddForm.signer_due_date,
					redact: easyAddForm.redact,
					visibility: easyAddForm.visibility,
					allowComments: easyAddForm.allow_comments,
					isActive: easyAddForm.is_active,
					requiresApproval: easyAddForm.requiresApproval,
				};
			});

		return [...formattedBasicForms, ...formattedExternalForms, ...formattedEasyAddForms];
	};

	// save the entire template
	const handleSaveTemplate = async () => {
		setIsSaving(true);
		const { basicForms, externalForms, easyAddForms } = localTemplateForms;
		const formattedForms = formatTemplateForms(basicForms, externalForms, easyAddForms);
		const templatePayload = {
			name: localTemplateName,
			assignments: formattedForms,
		};
		// create a new template
		if (!templateID) {
			const response = await dispatch(
				GenerateProcess("CreateTemplate", {
					payload: templatePayload,
					params: { ignoreFailed: true },
				})
			);
			if (response.status === 201) {
				const createdTemplateID = response.response.reviewTemplate.id;
				dispatch(ChangePage(`/edit-review-template/${createdTemplateID}`));
				setTemplateName(localTemplateName);
				setTemplateForms(localTemplateForms);
				dispatch(DisplaySuccess("Review Template was created successfully"));
				setIsSaving(false);
			} else {
				const error =
					response.response.get("errors", null) && response.response.get("errors").size > 0
						? response.response.get("errors").first()
						: "Something went wrong";
				dispatch(DisplayError(error));
				setIsSaving(false);
			}
		} else {
			// if templateID exists, save the edited template
			const response = await dispatch(
				GenerateProcess("EditTemplate", {
					templateID,
					payload: templatePayload,
				})
			);
			setTemplateName(localTemplateName);
			setTemplateForms(localTemplateForms);
			if (response.status === 201) {
				dispatch(DisplaySuccess("Review Template saved successfully"));
				setIsSaving(false);
			} else {
				const error =
					response.response.get("errors", null) && response.response.get("errors").size > 0
						? response.response.get("errors").first()
						: "Something went wrong";

				dispatch(DisplayError(error));
				setIsSaving(false);
			}
		}
	};

	const handleDeleteTemplate = async () => {
		dispatch(
			DisplayConfirmationModal("Are you sure you want to delete this template?", async () => {
				const request = await dispatch(GenerateProcess("DeleteTemplate", { templateID }));
				if (request.status === 200) {
					dispatch(HardPageLoad("/administration/reviews/templates/"));
				} else {
					dispatch(DisplayError("Review Template could not be deleted"));
				}
			})
		);
	};

	const handleMenuSelect = (selectedItem: string) => {
		if (selectedItem === "addForm") {
			setSelectedFormType("basicForms");
			handleAddForm("basicForms");
		}
		if (selectedItem === "addEasyAddForm") {
			setSelectedFormType("easyAddForms");
			handleAddForm("easyAddForms");
		}
		if (selectedItem === "addExternalForm") {
			setSelectedFormType("externalForms");
			handleAddForm("externalForms");
		}
		if (selectedItem === "save") {
			handleSaveTemplate();
		}
		if (selectedItem === "delete") {
			handleDeleteTemplate();
		}
	};

	// return the appropriate form card comonent depending on what the selected form type is
	const getFormComponents = (selectedForms: (BasicForm | ExternalForm | EasyAddForm)[]) => {
		switch (selectedFormType) {
			case "basicForms":
				return (selectedForms as BasicForm[]).map((form: BasicForm, formIndex) => {
					if (form.isFormDeleted) {
						return null;
					}
					const isSelectedFormDeleted = form.form?.isActive === false;
					return (
						<ReviewFormCard
							key={`${selectedFormType}-${formIndex}`}
							className={css.formCard}
							type="basic"
							isDeleted={isSelectedFormDeleted}
							form={form.form}
							assignment={form.assignment}
							isEditing={form.isEditing}
							editableForm={form.form}
							authorRequired={true}
							dateType="relative"
							onSave={(form) => handleSaveForm(form, formIndex)}
							onCancel={
								form.unsaved ? () => handleDeleteForm(formIndex) : () => toggleEditForm(formIndex)
							}
							onDelete={() => handleDeleteForm(formIndex)}
							onEdit={() => toggleEditForm(formIndex)}
							lockedFields={lockedFields}
							isNewForm={form.isNewForm}
						/>
					);
				});
			case "externalForms":
				return (selectedForms as ExternalForm[]).map((form: ExternalForm, formIndex) => {
					if (form.isFormDeleted) {
						return null;
					}
					const isSelectedFormDeleted = form.form?.isActive === false;

					return (
						<ReviewFormCard
							key={`${selectedFormType}-${formIndex}`}
							className={css.formCard}
							type="external"
							isDeleted={isSelectedFormDeleted}
							form={form.form}
							assignment={form.assignment}
							isEditing={form.isEditing}
							editableForm={form.form}
							onEdit={() => toggleEditForm(formIndex)}
							onCancel={
								form.unsaved ? () => handleDeleteForm(formIndex) : () => toggleEditForm(formIndex)
							}
							onDelete={() => handleDeleteForm(formIndex)}
							onSave={(form) => handleSaveForm(form, formIndex)}
							lockedFields={lockedFields}
							isNewForm={form.isNewForm}
						/>
					);
				});
			case "easyAddForms":
				return (selectedForms as EasyAddForm[]).map((form: EasyAddForm, formIndex) => {
					if (form.isFormDeleted) {
						return null;
					}
					const isSelectedFormDeleted = form.is_active === false;
					lockedFields.push("dateType");

					return (
						<EasyAddFormCard
							key={`${selectedFormType}-${formIndex}`}
							className={css.formCard}
							form={form}
							isDeleted={isSelectedFormDeleted}
							isEditing={form.isEditing}
							editableForm={form.formObject}
							lockedFields={lockedFields}
							onEdit={() => toggleEditForm(formIndex)}
							onSave={(form) => handleSaveForm(form, formIndex)}
							onCancel={
								form.unsaved ? () => handleDeleteForm(formIndex) : () => toggleEditForm(formIndex)
							}
							onDelete={() => handleDeleteForm(formIndex)}
							isNewForm={form.isNewForm}
						/>
					);
				});
			default:
				return null;
		}
	};

	return (
		<AdminContainer>
			<PageTitle title={`${templateID ? "Edit" : "Create"} Review Template - ${companyName} - PerformYard`} />

			<Breadcrumbs
				className={css.breadcrumbs}
				pages={[
					{
						pageID: "reviewTemplates",
						label: "Review Templates",
						url: "/administration/reviews/templates/",
					},
					{
						pageID: "editReviewTemplate",
						label: templateName,
						url: `/edit-review-template/${templateID || ""}`,
					},
				]}
			/>

			<Spacer height={4} />

			<div className={css.headerContent}>
				<AdminTitle
					includeSpacer={false}
					description={
						templateID
							? "Modify a review template that can be used to create review cycles and reviews. Add one or more forms to your review template below."
							: "Create a review template that can be used to create review cycles and reviews. Add one or more forms to your review template below."
					}
				>
					{templateID ? "Edit Template" : "Create New Template"}
				</AdminTitle>

				<DropDownMenu options={menuOptions} onChange={handleMenuSelect} />
			</div>

			<div className={css.templateContent}>
				<Grid cols={3}>
					<GridCol>
						<div className={css.leftSideContent}>
							<div className={css.label}>Template Name</div>
							<TextInput value={localTemplateName} onChange={(value) => setLocalTemplateName(value)} />
							<Button
								className={css.saveTemplateButton}
								color="green"
								onClick={handleSaveTemplate}
								isRequesting={isSaving}
							>
								Save
							</Button>
						</div>
					</GridCol>

					<GridCol span={2}>
						<div className={css.rightSideContent}>
							<div className={css.label}>Template Forms</div>
							<Tabs
								className={css.tabs}
								activeTitle={selectedFormType}
								titles={tabTitles}
								onSelectTitle={(titleId: string) => {
									if (["basicForms", "externalForms", "easyAddForms"].includes(titleId)) {
										setSelectedFormType(titleId as TemplateFormKeys);
									}
								}}
							/>

							{formsComponents}

							{duplicateEasyAdd ? (
								<ShowError
									error={{
										message:
											'A 360 form with the same "Who can add this form" field already exists.',
									}}
								/>
							) : null}

							<div>
								<div className={css.addFormButton} onClick={() => handleAddForm(selectedFormType)}>
									Add New Form
								</div>
							</div>
						</div>
					</GridCol>
				</Grid>
			</div>
			<BeforeUnload checkForUnsavedData={templateHasChanges} />
		</AdminContainer>
	);
};

export default EditReviewTemplate;
